import { useState, type ReactNode } from 'react';

export interface LegendItem {
  label: string;
  color: string;
  kind?: 'line' | 'rect' | 'dot';
}

export interface TableData {
  columns: string[];
  rows: (string | number)[][];
}

interface Props {
  title: string;
  subtitle?: string;
  legend?: LegendItem[];
  table?: TableData | null;
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
}

/** Figure wrapper: title, legend, and a table-view twin for every chart. */
export function ChartFrame({ title, subtitle, legend, table, actions, className, children }: Props) {
  const [showTable, setShowTable] = useState(false);
  return (
    <figure className={`chart-frame panel ${className ?? ''}`}>
      <figcaption className="chart-head">
        <div>
          <h3 className="chart-title">{title}</h3>
          {subtitle && <p className="chart-sub">{subtitle}</p>}
        </div>
        <div className="chart-actions">
          {actions}
          {table && (
            <button type="button" className="ghost-btn" onClick={() => setShowTable((v) => !v)} aria-pressed={showTable}>
              {showTable ? 'Chart' : 'Table'}
            </button>
          )}
        </div>
      </figcaption>
      {legend && legend.length > 1 && (
        <ul className="legend" aria-label="Legend">
          {legend.map((l) => (
            <li key={l.label}>
              <span className={`legend-key legend-${l.kind ?? 'rect'}`} style={{ background: l.color }} aria-hidden />
              {l.label}
            </li>
          ))}
        </ul>
      )}
      {showTable && table ? <DataTable table={table} /> : children}
    </figure>
  );
}

export function DataTable({ table }: { table: TableData }) {
  return (
    <div className="table-wrap">
      <table className="data-table">
        <thead>
          <tr>
            {table.columns.map((c) => (
              <th key={c} scope="col">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Tooltip({ x, y, width, children }: { x: number; y: number; width: number; children: ReactNode }) {
  const flip = x > width - 200;
  return (
    <div
      className="viz-tooltip"
      role="status"
      style={{ left: flip ? undefined : x + 14, right: flip ? width - x + 14 : undefined, top: Math.max(0, y - 10) }}
    >
      {children}
    </div>
  );
}

export function Shape({
  shape,
  x,
  y,
  r = 4,
  color,
  ring = 'var(--surface)',
}: {
  shape: 'circle' | 'square' | 'diamond' | 'triangle';
  x: number;
  y: number;
  r?: number;
  color: string;
  ring?: string;
}) {
  const common = { fill: color, stroke: ring, strokeWidth: 2, paintOrder: 'stroke' as const };
  switch (shape) {
    case 'square':
      return <rect x={x - r} y={y - r} width={r * 2} height={r * 2} rx={1.5} {...common} />;
    case 'diamond':
      return <path d={`M${x},${y - r - 1}L${x + r + 1},${y}L${x},${y + r + 1}L${x - r - 1},${y}Z`} {...common} />;
    case 'triangle':
      return <path d={`M${x},${y - r - 1}L${x + r + 1},${y + r}L${x - r - 1},${y + r}Z`} {...common} />;
    default:
      return <circle cx={x} cy={y} r={r} {...common} />;
  }
}
